import React from 'react';
import { useQuery } from 'convex/react';
import { api } from '../../lib/convex';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Calendar, ArrowRight, Loader2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import DOMPurify from 'dompurify';

export default function BlogSection() {
  const latestPosts = useQuery(api.blog.getLatestBlogPosts, { count: 3 });
  
  if (!latestPosts) {
    return (
      <div className="py-8 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <Loader2 className="w-8 h-8 mx-auto text-blue-600 animate-spin" />
          </div>
        </div>
      </div>
    );
  }
  
  // Skip rendering if no posts
  if (latestPosts.length === 0) {
    return null;
  }
  
  return (
    <section className="py-12 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold mb-3">Blog & Resources</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Insights, tips, and strategies for maximizing your content marketing success
          </p>
        </div>
        
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {latestPosts.map((post) => {
            // Sanitize all user-generated content
            const safeTitle = DOMPurify.sanitize(post.title);
            const safeSummary = DOMPurify.sanitize(post.summary);
            const safeImageUrl = post.imageUrl ? DOMPurify.sanitize(post.imageUrl) : undefined;
            const safeSlug = DOMPurify.sanitize(post.slug);
            
            return (
              <article key={post._id} className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-100 hover:shadow-lg transition-shadow">
                <div className="h-44 bg-gray-100">
                  {safeImageUrl ? (
                    <img 
                      src={safeImageUrl} 
                      alt={safeTitle}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-blue-50">
                      <span className="text-blue-500 font-medium">AISEOBlogGenerator</span>
                    </div>
                  )}
                </div>
                <div className="p-5">
                  <h3 className="font-bold text-xl mb-2 line-clamp-2">
                    <Link 
                      to={`/blog/${safeSlug}`}
                      className="hover:text-blue-600 transition-colors"
                    >
                      {safeTitle}
                    </Link>
                  </h3>
                  <p className="text-gray-600 mb-3 line-clamp-2">
                    {safeSummary}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-500 flex items-center">
                      <Calendar className="w-4 h-4 mr-1" />
                      {post.publishedAt && formatDistanceToNow(post.publishedAt, { addSuffix: true })}
                    </div>
                    <Link to={`/blog/${safeSlug}`}>
                      <Button variant="ghost" size="sm" className="p-0 h-auto text-blue-600 hover:text-blue-800">
                        Read More
                        <ArrowRight className="w-4 h-4 ml-1" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
        
        <div className="text-center">
          <Link to="/blog">
            <Button variant="outline" size="lg">
              View All Articles
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}